import { supabase } from './supabaseClient'

export type PushPermissionState = 'unsupported' | 'denied' | 'prompt' | 'subscribed' | 'default'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY?.trim() ?? ''

export function isPushSupported(): boolean {
  return typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
}

export function isPushConfigured(): boolean {
  return VAPID_PUBLIC_KEY.length > 0
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    await navigator.serviceWorker.ready
    return registration
  } catch (error) {
    console.warn('Service worker registration failed:', error)
    return null
  }
}

export async function getPushPermissionState(): Promise<PushPermissionState> {
  if (!isPushSupported() || !isPushConfigured()) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  if (Notification.permission === 'granted') {
    const registration = await registerServiceWorker()
    const subscription = await registration?.pushManager.getSubscription()
    return subscription ? 'subscribed' : 'default'
  }
  return 'prompt'
}

type RegisterPushResponse = {
  ok?: boolean
  registered?: boolean
  error?: string
}

export async function enablePushNotifications(): Promise<{ ok: boolean; error?: string }> {
  if (!isPushSupported()) {
    return { ok: false, error: 'Push notifications are not supported in this browser.' }
  }
  if (!isPushConfigured()) {
    return { ok: false, error: 'Push notifications are not configured for this build.' }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return { ok: false, error: 'Notification permission was denied.' }
  }

  const registration = await registerServiceWorker()
  if (!registration) {
    return { ok: false, error: 'Could not register the service worker.' }
  }

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    })
  }

  const { data, error } = await supabase.functions.invoke<RegisterPushResponse>(
    'register-push-subscription',
    { body: { action: 'register', subscription: subscription.toJSON() } },
  )

  if (error) return { ok: false, error: error.message }
  if (!data?.ok) return { ok: false, error: data?.error ?? 'Failed to register push subscription' }

  return { ok: true }
}

export async function disablePushNotifications(): Promise<{ ok: boolean; error?: string }> {
  if (!isPushSupported()) return { ok: true }

  const registration = await navigator.serviceWorker.getRegistration('/')
  const subscription = await registration?.pushManager.getSubscription()

  if (subscription) {
    const endpoint = subscription.endpoint
    await subscription.unsubscribe()
    const { error } = await supabase.functions.invoke<RegisterPushResponse>(
      'register-push-subscription',
      { body: { action: 'unregister', endpoint } },
    )
    if (error) return { ok: false, error: error.message }
  }

  return { ok: true }
}

/** Prompt once after calendar connect when push is available but not yet enabled. */
export function shouldPromptForPushAfterCalendarConnect(): boolean {
  if (!isPushSupported() || !isPushConfigured()) return false
  if (Notification.permission !== 'default') return false
  return localStorage.getItem('kilo_push_prompt_dismissed') !== '1'
}

export function dismissPushPrompt(): void {
  localStorage.setItem('kilo_push_prompt_dismissed', '1')
}

export async function ensureServiceWorkerRegistered(): Promise<void> {
  if (!isPushSupported()) return
  await registerServiceWorker()
}
